import React, {useEffect, useState} from 'react'
import {useDisclosure} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

import {useToast} from '@app/hooks/use-toast'
import {useDeliveryCountryDecision} from '@app/custom-contexts'
import {getCountryNameByCode, sortShipToCountries} from '@app/utils/ship-to-country-utils'
import CountrySelectorModalContent from '@app/components/country-selector-modal-content'
import {useApplyShipToCountry} from '@app/hooks/use-apply-ship-to-country'

/**
 * Controls the delivery-country confirmation modal shown after SSR geo dispatch.
 *
 * The component hydrates the SSR decision, lets the shopper accept or change the
 * suggested country, updates shopper context/basket assignment, and performs
 * cross-site or partner redirects when the selected country requires them.
 *
 * @component
 * @param {object} props - Component props.
 * @param {Function} props.setIsSetShipToCountryEnabled - Updates the app-level readiness flag.
 * @returns {React.ReactElement} Country selector modal content.
 */
const CountrySelectorModal = ({setIsSetShipToCountryEnabled, onDismiss}) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [geolocCountry, setGeolocCountry] = useState()
    const [geolocCountryCode, setGeolocCountryCode] = useState()
    const [selectedCountryCode, setSelectedCountryCode] = useState()
    const [isLoading, setIsLoading] = useState(false)

    const toast = useToast()
    const deliveryCountryDecision = useDeliveryCountryDecision()
    const intl = useIntl()

    const {applyShipToCountry, shopperContext, isShopperContextLoading, shipToCountry} =
        useApplyShipToCountry()

    useEffect(() => {
        if (isShopperContextLoading) return

        const hasShopperContextCountry = Boolean(
            (shopperContext?.geoLocation?.countryCode || shopperContext?.geoLocation?.country) &&
                shopperContext?.customQualifiers?.locale &&
                shopperContext?.customQualifiers?.currency
        )

        setIsSetShipToCountryEnabled(
            Boolean(hasShopperContextCountry || deliveryCountryDecision?.shipToCountry?.id)
        )
    }, [
        deliveryCountryDecision?.shipToCountry?.id,
        isShopperContextLoading,
        shopperContext,
        setIsSetShipToCountryEnabled
    ])

    useEffect(() => {
        const suggestedCountryCode =
            deliveryCountryDecision?.suggestedCountry ||
            deliveryCountryDecision?.geoCountry ||
            deliveryCountryDecision?.shipToCountry?.id ||
            shipToCountry?.id

        if (!suggestedCountryCode) return

        setGeolocCountryCode(suggestedCountryCode)
        setSelectedCountryCode((currentCountryCode) => currentCountryCode || suggestedCountryCode)
        setGeolocCountry(getCountryNameByCode(suggestedCountryCode) || suggestedCountryCode)

        if (deliveryCountryDecision?.shouldShowCountryModal) {
            onOpen()
        }
    }, [
        deliveryCountryDecision?.geoCountry,
        deliveryCountryDecision?.shipToCountry?.id,
        deliveryCountryDecision?.shouldShowCountryModal,
        deliveryCountryDecision?.suggestedCountry,
        onOpen,
        shipToCountry?.id
    ])

    /**
     * Tracks the shopper-selected country from the modal select field.
     *
     * @param {React.ChangeEvent<HTMLSelectElement>} event - Select change event.
     */
    const handleCountryChange = (event) => {
        setSelectedCountryCode(event.target.value)
    }

    /**
     * Confirms the selected country and applies the required navigation/context update.
     *
     * @returns {Promise<void>} Resolves after the selected country path has been handled.
     */
    const handleConfirm = async () => {
        if (!selectedCountryCode) return

        setIsLoading(true)

        try {
            const result = await applyShipToCountry(selectedCountryCode, {
                accepted: true
            })

            if (result.href) {
                onDismiss?.()
                window.location.href = result.href
                return
            }

            toast({
                title: 'Country updated successfully.',
                description: '',
                status: 'success'
            })

            setIsSetShipToCountryEnabled(true)
            onClose()
            onDismiss?.()
            window.location.reload()
        } catch (error) {
            toast({
                title: 'An error occurred.',
                description: error.message,
                status: 'error'
            })
        } finally {
            setIsLoading(false)
        }
    }

    const countries = sortShipToCountries(intl)

    return (
        <CountrySelectorModalContent
            isOpen={isOpen}
            onClose={() => {}}
            geolocCountry={geolocCountry}
            geolocCountryCode={geolocCountryCode}
            shipToCountryCode={shipToCountry?.id || ''}
            selectedCountryCode={selectedCountryCode}
            countries={countries}
            handleCountryChange={handleCountryChange}
            setSelectedCountryCode={setSelectedCountryCode}
            handleConfirm={handleConfirm}
            isLoading={isLoading}
            isCheckout={false}
            isConfirmDisabled={!selectedCountryCode}
        />
    )
}

CountrySelectorModal.propTypes = {
    setIsSetShipToCountryEnabled: PropTypes.func.isRequired,
    onDismiss: PropTypes.func
}

export default CountrySelectorModal
